import { supabase } from "@/integrations/supabase/client";

/**
 * Checks admin role with a verified session.
 *
 * The `has_role` function is only executable by the `authenticated` role, so a
 * call made while the JWT is missing/expired fails with
 * "permission denied for function has_role" — which used to be treated as
 * "not an admin" and bounced real admins out of the admin area.
 *
 * Returns `null` when the check could not be completed (transient error), so
 * callers can distinguish "definitely not admin" from "unknown".
 */
export async function checkIsAdmin(userId?: string): Promise<boolean | null> {
  let id = userId;

  // Make sure we have a live (refreshed) session before calling the RPC.
  let { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) {
    const { data: refreshed } = await supabase.auth.refreshSession();
    sessionData = refreshed as typeof sessionData;
  }
  if (!sessionData.session) return null;
  id = id ?? sessionData.session.user.id;

  const call = () => supabase.rpc("has_role", { _user_id: id!, _role: "admin" });

  let { data, error } = await call();
  if (error) {
    // Token may have just expired mid-flight — refresh once and retry.
    const { data: refreshed } = await supabase.auth.refreshSession();
    if (!refreshed?.session) return null;
    ({ data, error } = await call());
    if (error) return null;
  }
  return !!data;
}
